import { useState } from "react"
import { Calendar, Pencil, Store, Trash2 } from "lucide-react"
import { Avatar } from "@/components/ui/Avatar"
import { Button } from "@/components/ui/Button"
import type { Person } from "@/lib/person"
import { shortDate } from "../format"
import type { MentionNames, ReviewAction } from "../types"
import NoteText from "./NoteText"

function dateRange(from: string | null, to: string | null) {
  if (!from) return to ? `Until ${shortDate(to)}` : null
  return !to || to === from ? shortDate(from) : `${shortDate(from)} – ${shortDate(to)}`
}

interface Props {
  action: ReviewAction
  people: Person[]
  names: MentionNames
  onEdit: () => void
  onDelete: () => void
}

// One saved action, read-only. Edit and delete stay visible in a quiet tone;
// delete asks once more before it goes.
export default function ActionRow({ action, people, names, onEdit, onDelete }: Props) {
  const [confirming, setConfirming] = useState(false)
  const dates = dateRange(action.starts_on, action.ends_on)
  const assigned = people.filter((p) => action.assignee_ids.includes(p.id))
  return (
    <div>
      {/* Note on the first line, details on the second. The details keep
          fixed widths, even when empty, so they line up from row to row. */}
      <div className="flex items-start gap-4 px-4 py-3 text-sm">
        <span className="flex h-8 w-40 shrink-0 items-center font-semibold">{action.action_type}</span>
        <div className="min-w-0 flex-1">
          <div className="whitespace-pre-line py-1.5">
            {action.note ? <NoteText note={action.note} names={names} /> : <span className="text-muted-foreground">No note</span>}
          </div>
          <div className="flex items-center gap-4">
            <span className="flex h-7 w-32 shrink-0 items-center gap-1">
              <Store className="h-4 w-4 shrink-0" />
              <span className="truncate">{action.channel_name ?? "All channels"}</span>
            </span>
            <span className="flex h-7 w-36 shrink-0 items-center gap-1 tabular-nums">
              {dates && (
                <>
                  <Calendar className="h-4 w-4 shrink-0" />
                  {dates}
                </>
              )}
            </span>
            {assigned.length > 0 && (
              <span className="flex h-7 min-w-0 items-center gap-1.5">
                <span className="flex shrink-0 -space-x-1">
                  {assigned.map((p) => (
                    <Avatar key={p.id} name={p.name} initials={p.initials} avatarUrl={p.avatar_url} tone="primary" className="ring-2 ring-background" />
                  ))}
                </span>
                <span className="truncate">{assigned.map((p) => p.name.split(" ")[0]).join(", ")}</span>
              </span>
            )}
          </div>
        </div>
        <span className="flex shrink-0 items-center">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={onEdit} aria-label="Edit action">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => setConfirming(true)} aria-label="Delete action">
            <Trash2 className="h-4 w-4" />
          </Button>
        </span>
      </div>
      {confirming && (
        <div className="flex items-center gap-2 border-t border-border bg-destructive/5 px-4 py-2.5 text-sm text-foreground">
          <span>Delete this action?</span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={onDelete}>Delete</Button>
          </div>
        </div>
      )}
    </div>
  )
}
